import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Toaster, toast } from 'sonner';
import { Star, ArrowLeft, CircleNotch, Trophy, Target, Trash, CheckCircle, XCircle, MinusCircle } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HitBadge = ({ type }) => {
  const styles = {
    EXACT: 'bg-emerald-500 text-white',
    TOP5: 'bg-blue-500 text-white',
    CROSS: 'bg-amber-500 text-white',
    MISS: 'bg-gray-300 text-gray-600',
    'N/A': 'bg-gray-200 text-gray-400',
  };
  const icons = {
    EXACT: <CheckCircle size={12} weight="bold" />,
    TOP5: <Target size={12} weight="bold" />,
    CROSS: <MinusCircle size={12} weight="bold" />,
    MISS: <XCircle size={12} weight="bold" />,
  };
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold ${styles[type] || styles.MISS}`} data-testid={`hit-badge-${type}`}>
      {icons[type]} {type}
    </span>
  );
};

const BallCircle = ({ num, isHit, isLS }) => (
  <span className={`inline-flex w-8 h-8 rounded-full items-center justify-center font-mono font-bold text-sm ${isHit ? 'bg-emerald-500 text-white ring-2 ring-emerald-300' : isLS ? 'bg-yellow-100 text-yellow-800' : 'bg-[#002FA7]/10 text-[#002FA7]'}`}>
    {num}
  </span>
);

export default function DrawValidationTracker() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [scoreboard, setScoreboard] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [activeTab, setActiveTab] = useState('submit');

  // Form state
  const [drawDate, setDrawDate] = useState('');
  const [balls, setBalls] = useState({ b1: '', b2: '', b3: '', b4: '', b5: '' });
  const [ls, setLs] = useState({ ls1: '', ls2: '' });

  const loadHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/validation/history`);
      setHistory(res.data.validations || []);
    } catch { /* ignore */ }
  }, []);

  const loadScoreboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/validation/scoreboard`);
      setScoreboard(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadHistory();
    loadScoreboard();
  }, [loadHistory, loadScoreboard]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/validation/submit-draw`, {
        draw_date: drawDate,
        ball_1: parseInt(balls.b1), ball_2: parseInt(balls.b2), ball_3: parseInt(balls.b3),
        ball_4: parseInt(balls.b4), ball_5: parseInt(balls.b5),
        lucky_star_1: parseInt(ls.ls1), lucky_star_2: parseInt(ls.ls2),
      });
      if (res.data.error) {
        toast.error(res.data.error);
      } else {
        toast.success(`Validation for ${drawDate} submitted successfully`);
        setLastResult(res.data);
        setActiveTab('results');
        loadHistory();
        loadScoreboard();
        // Reset form
        setBalls({ b1: '', b2: '', b3: '', b4: '', b5: '' });
        setLs({ ls1: '', ls2: '' });
        setDrawDate('');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (date) => {
    if (!window.confirm(`Delete validation for ${date}?`)) return;
    try {
      await axios.delete(`${API}/validation/${date}`);
      toast.success('Deleted');
      loadHistory();
      loadScoreboard();
    } catch { toast.error('Failed to delete'); }
  };

  const ballLabels = { ball_1: 'B1', ball_2: 'B2', ball_3: 'B3', ball_4: 'B4', ball_5: 'B5' };
  const lsLabels = { lucky_star_1: 'LS1', lucky_star_2: 'LS2' };

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-btn"><ArrowLeft size={18} /></Button>
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded flex items-center justify-center"><Trophy size={24} weight="fill" className="text-white" /></div>
              <div><h1 className="text-2xl font-bold text-gray-900">W52: Draw Validation Tracker</h1><p className="text-xs text-gray-500">Compare actual results vs. window predictions</p></div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-white border border-gray-200 p-1">
            <TabsTrigger value="submit" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white font-bold" data-testid="tab-submit">Enter Draw Result</TabsTrigger>
            <TabsTrigger value="results" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white font-bold" data-testid="tab-results">Last Comparison</TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white font-bold" data-testid="tab-history">History ({history.length})</TabsTrigger>
            <TabsTrigger value="scoreboard" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white font-bold" data-testid="tab-scoreboard">Window Scoreboard</TabsTrigger>
          </TabsList>

          {/* SUBMIT TAB */}
          <TabsContent value="submit">
            <div className="max-w-2xl mx-auto">
              <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-xl p-6 space-y-6" data-testid="draw-entry-form">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Draw Date</label>
                  <input type="date" value={drawDate} onChange={e => setDrawDate(e.target.value)} required
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 outline-none" data-testid="input-draw-date" />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Main Balls (1-50)</label>
                  <div className="grid grid-cols-5 gap-3">
                    {[1,2,3,4,5].map(i => (
                      <div key={i} className="text-center">
                        <span className="text-[10px] font-bold text-gray-500 uppercase">Ball {i}</span>
                        <input type="number" min="1" max="50" required value={balls[`b${i}`]}
                          onChange={e => setBalls(prev => ({...prev, [`b${i}`]: e.target.value}))}
                          className="w-full border rounded-lg px-3 py-2.5 text-center font-mono font-bold text-lg focus:ring-2 focus:ring-emerald-500 outline-none"
                          data-testid={`input-ball-${i}`} />
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Lucky Stars (1-12)</label>
                  <div className="grid grid-cols-2 gap-3 max-w-xs">
                    {[1,2].map(i => (
                      <div key={i} className="text-center">
                        <span className="text-[10px] font-bold text-yellow-600 uppercase">LS {i}</span>
                        <input type="number" min="1" max="12" required value={ls[`ls${i}`]}
                          onChange={e => setLs(prev => ({...prev, [`ls${i}`]: e.target.value}))}
                          className="w-full border border-yellow-300 rounded-lg px-3 py-2.5 text-center font-mono font-bold text-lg focus:ring-2 focus:ring-yellow-500 outline-none bg-yellow-50"
                          data-testid={`input-ls-${i}`} />
                      </div>
                    ))}
                  </div>
                </div>

                <Button type="submit" disabled={submitting}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 text-base" data-testid="submit-draw-btn">
                  {submitting ? <><CircleNotch size={18} className="animate-spin mr-2" /> Analyzing all windows...</> : 'Submit & Compare Against All Windows'}
                </Button>
              </form>

              {/* Scoring legend */}
              <div className="mt-4 bg-white border rounded-lg p-4">
                <h4 className="text-xs font-bold text-gray-500 uppercase mb-2">Scoring System</h4>
                <div className="grid grid-cols-4 gap-2 text-xs">
                  <div className="flex items-center gap-1.5"><HitBadge type="EXACT" /> <span className="text-gray-600">3 pts — #1 prediction matches actual</span></div>
                  <div className="flex items-center gap-1.5"><HitBadge type="TOP5" /> <span className="text-gray-600">2 pts — Number in top 5 predictions</span></div>
                  <div className="flex items-center gap-1.5"><HitBadge type="CROSS" /> <span className="text-gray-600">1 pt — Predicted number appeared in draw</span></div>
                  <div className="flex items-center gap-1.5"><HitBadge type="MISS" /> <span className="text-gray-600">0 pts — No match</span></div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* RESULTS TAB */}
          <TabsContent value="results">
            {lastResult ? (
              <ResultsView data={lastResult} ballLabels={ballLabels} lsLabels={lsLabels} />
            ) : (
              <div className="text-center py-20 text-gray-500">Submit a draw to see comparison results</div>
            )}
          </TabsContent>

          {/* HISTORY TAB */}
          <TabsContent value="history">
            {history.length === 0 ? (
              <div className="text-center py-20 text-gray-500">No validations yet. Submit your first draw result.</div>
            ) : (
              <div className="space-y-4">
                {history.map(h => (
                  <div key={h.draw_date} className="bg-white border rounded-lg overflow-hidden" data-testid={`history-${h.draw_date}`}>
                    <div className="bg-slate-800 px-5 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-white font-bold">{h.draw_date}</span>
                        <div className="flex items-center gap-1.5">
                          {Object.entries(h.actual_draw || {}).map(([k, v]) => (
                            <BallCircle key={k} num={v} isLS={k.startsWith('lucky')} />
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-white/60 text-xs">{h.total_windows_tested} windows tested</span>
                        <Button variant="ghost" size="sm" onClick={() => { setLastResult(h); setActiveTab('results'); }} className="text-white/70 hover:text-white text-xs" data-testid={`view-${h.draw_date}`}>
                          View Details
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(h.draw_date)} className="text-red-400 hover:text-red-300" data-testid={`delete-${h.draw_date}`}>
                          <Trash size={16} />
                        </Button>
                      </div>
                    </div>
                    {/* Quick summary of top 3 windows */}
                    <div className="px-5 py-3">
                      <div className="flex items-center gap-4">
                        {(h.window_scores || []).slice(0, 5).map((ws, i) => (
                          <div key={ws.window_name} className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs ${i === 0 ? 'bg-emerald-100 text-emerald-800 font-bold' : 'bg-gray-100 text-gray-600'}`}>
                            {i === 0 && <Trophy size={12} weight="fill" className="text-emerald-600" />}
                            <span>{ws.window_name}</span>
                            <span className="font-mono font-bold">{ws.total_score}/{ws.max_score}</span>
                            <span className="text-gray-400">({ws.pct}%)</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* SCOREBOARD TAB */}
          <TabsContent value="scoreboard">
            {scoreboard && scoreboard.total_draws > 0 ? (
              <ScoreboardView scoreboard={scoreboard} ballLabels={ballLabels} lsLabels={lsLabels} />
            ) : (
              <div className="text-center py-20 text-gray-500">Submit at least one draw validation to see the scoreboard</div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function ResultsView({ data, ballLabels, lsLabels }) {
  const mainScores = (data.window_scores || []).filter(w => w.type === 'main');
  const lsScores = (data.window_scores || []).filter(w => w.type === 'ls');
  const allKeys = { ...ballLabels, ...lsLabels };

  return (
    <div className="space-y-6">
      {/* Actual Draw Banner */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-xl p-5 text-white" data-testid="actual-draw-banner">
        <div className="text-xs font-bold text-white/50 uppercase tracking-wider mb-2">Actual Draw — {data.draw_date}</div>
        <div className="flex items-center gap-3">
          {['ball_1','ball_2','ball_3','ball_4','ball_5'].map(bk => (
            <div key={bk} className="w-14 h-14 rounded-full bg-white flex items-center justify-center">
              <span className="font-mono font-bold text-xl text-slate-800">{data.actual_draw?.[bk]}</span>
            </div>
          ))}
          <div className="w-px h-10 bg-white/20 mx-2" />
          {['lucky_star_1','lucky_star_2'].map(lk => (
            <div key={lk} className="w-14 h-14 rounded-full bg-yellow-400 flex items-center justify-center">
              <span className="font-mono font-bold text-xl text-yellow-900">{data.actual_draw?.[lk]}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Main Ball Windows */}
      <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide">Main Ball Windows — Sorted by Score</h3>
      <WindowScoreTable scores={mainScores} keys={Object.keys(ballLabels)} labels={ballLabels} />

      {/* LS Windows */}
      {lsScores.length > 0 && (
        <>
          <h3 className="text-sm font-bold text-yellow-600 uppercase tracking-wide flex items-center gap-1"><Star size={14} weight="fill" /> Lucky Star Windows</h3>
          <WindowScoreTable scores={lsScores} keys={Object.keys(lsLabels)} labels={lsLabels} />
        </>
      )}
    </div>
  );
}

function WindowScoreTable({ scores, keys, labels }) {
  return (
    <div className="bg-white border rounded-lg overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-gray-100">
          <tr>
            <th className="px-4 py-2.5 text-left text-[10px] uppercase text-gray-600 font-bold w-56">Window</th>
            <th className="px-3 py-2.5 text-center text-[10px] uppercase text-gray-600 font-bold w-20">Score</th>
            <th className="px-3 py-2.5 text-center text-[10px] uppercase text-gray-600 font-bold w-16">%</th>
            {keys.map(k => (
              <th key={k} className="px-3 py-2.5 text-center text-[10px] uppercase text-gray-600 font-bold">{labels[k]}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {scores.map((ws, idx) => {
            const rowBg = ws.pct >= 40 ? 'bg-emerald-50' : ws.pct >= 20 ? 'bg-blue-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50';
            return (
              <tr key={ws.window_name} className={rowBg} data-testid={`score-row-${ws.window_id}`}>
                <td className="px-4 py-2 border-b">
                  <div className="flex items-center gap-2">
                    {idx === 0 && <Trophy size={14} weight="fill" className="text-amber-500" />}
                    <span className="font-semibold text-gray-800">{ws.window_name}</span>
                  </div>
                </td>
                <td className="px-3 py-2 border-b text-center">
                  <span className={`font-mono font-bold ${ws.total_score > 0 ? 'text-emerald-700' : 'text-gray-400'}`}>
                    {ws.total_score}/{ws.max_score}
                  </span>
                </td>
                <td className="px-3 py-2 border-b text-center">
                  <span className={`font-mono font-bold text-sm ${ws.pct >= 40 ? 'text-emerald-600' : ws.pct >= 20 ? 'text-blue-600' : 'text-gray-500'}`}>
                    {ws.pct}%
                  </span>
                </td>
                {keys.map(k => {
                  const br = ws.per_ball?.[k] || {};
                  return (
                    <td key={k} className="px-3 py-2 border-b text-center">
                      <div className="flex flex-col items-center gap-0.5">
                        <div className="flex items-center gap-1">
                          {(br.predicted_top5 || []).slice(0, 3).map((n, ni) => (
                            <span key={ni} className={`inline-flex w-6 h-6 rounded-full items-center justify-center font-mono text-[10px] font-bold ${n === br.actual ? 'bg-emerald-500 text-white ring-1 ring-emerald-300' : 'bg-gray-100 text-gray-600'}`}>{n}</span>
                          ))}
                        </div>
                        <HitBadge type={br.hit_type || 'MISS'} />
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ScoreboardView({ scoreboard, ballLabels, lsLabels }) {
  const mainBoard = (scoreboard.scoreboard || []).filter(s => s.type === 'main');
  const lsBoard = (scoreboard.scoreboard || []).filter(s => s.type === 'ls');

  return (
    <div className="space-y-6">
      {/* Summary banner */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-5 text-white" data-testid="scoreboard-summary">
        <div className="flex items-center gap-3 mb-2">
          <Trophy size={28} weight="fill" className="text-amber-300" />
          <div>
            <h2 className="text-xl font-bold">Cumulative Window Scoreboard</h2>
            <p className="text-xs text-white/60">{scoreboard.total_draws} draw(s) validated: {(scoreboard.draw_dates || []).join(', ')}</p>
          </div>
        </div>
        {/* Top 3 windows */}
        <div className="grid grid-cols-3 gap-3 mt-4">
          {mainBoard.slice(0, 3).map((w, i) => (
            <div key={w.window_name} className={`rounded-lg p-3 ${i === 0 ? 'bg-amber-400/20 border border-amber-300/40' : 'bg-white/10'}`}>
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-lg">{i === 0 ? '1st' : i === 1 ? '2nd' : '3rd'}</span>
                <span className="font-bold text-sm">{w.window_name}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono font-bold text-2xl">{w.pct}%</span>
                <div className="text-[10px] text-white/60">
                  <div>{w.exact_hits} exact | {w.top5_hits} top5 | {w.cross_hits} cross</div>
                  <div>{w.misses} misses</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Full main scoreboard */}
      <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide">Main Ball Windows — All-time Accuracy</h3>
      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-2.5 text-left text-[10px] uppercase font-bold w-52">Window</th>
              <th className="px-3 py-2.5 text-center text-[10px] uppercase font-bold">Score</th>
              <th className="px-3 py-2.5 text-center text-[10px] uppercase font-bold">Accuracy</th>
              <th className="px-3 py-2.5 text-center text-[10px] uppercase font-bold">Exact</th>
              <th className="px-3 py-2.5 text-center text-[10px] uppercase font-bold">Top5</th>
              <th className="px-3 py-2.5 text-center text-[10px] uppercase font-bold">Cross</th>
              <th className="px-3 py-2.5 text-center text-[10px] uppercase font-bold">Miss</th>
              {Object.entries(ballLabels).map(([k, l]) => (
                <th key={k} className="px-3 py-2.5 text-center text-[10px] uppercase font-bold">{l} %</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {mainBoard.map((w, idx) => {
              const barColor = w.pct >= 40 ? 'bg-emerald-500' : w.pct >= 20 ? 'bg-blue-500' : 'bg-gray-400';
              return (
                <tr key={w.window_name} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`sb-${w.window_name}`}>
                  <td className="px-4 py-2 border-b">
                    <div className="flex items-center gap-2">
                      {idx === 0 && <Trophy size={14} weight="fill" className="text-amber-500" />}
                      <span className="font-semibold">{w.window_name}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 border-b text-center font-mono font-bold">{w.total_score}/{w.max_score}</td>
                  <td className="px-3 py-2 border-b text-center">
                    <div className="flex items-center gap-1.5">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full"><div className={`h-full rounded-full ${barColor}`} style={{width: `${w.pct}%`}} /></div>
                      <span className="font-mono font-bold text-sm w-12 text-right">{w.pct}%</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 border-b text-center font-mono text-emerald-600 font-bold">{w.exact_hits}</td>
                  <td className="px-3 py-2 border-b text-center font-mono text-blue-600 font-bold">{w.top5_hits}</td>
                  <td className="px-3 py-2 border-b text-center font-mono text-amber-600">{w.cross_hits}</td>
                  <td className="px-3 py-2 border-b text-center font-mono text-gray-400">{w.misses}</td>
                  {Object.keys(ballLabels).map(bk => {
                    const pb = w.per_ball?.[bk] || {};
                    return (
                      <td key={bk} className="px-3 py-2 border-b text-center">
                        <span className={`font-mono font-bold text-xs ${pb.pct >= 40 ? 'text-emerald-600' : pb.pct >= 20 ? 'text-blue-600' : 'text-gray-500'}`}>{pb.pct || 0}%</span>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* LS Scoreboard */}
      {lsBoard.length > 0 && (
        <>
          <h3 className="text-sm font-bold text-yellow-600 uppercase tracking-wide flex items-center gap-1"><Star size={14} weight="fill" /> Lucky Star Windows — All-time Accuracy</h3>
          <div className="bg-white border rounded-lg overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2.5 text-left text-[10px] uppercase font-bold w-52">Window</th>
                  <th className="px-3 py-2.5 text-center text-[10px] uppercase font-bold">Score</th>
                  <th className="px-3 py-2.5 text-center text-[10px] uppercase font-bold">Accuracy</th>
                  <th className="px-3 py-2.5 text-center text-[10px] uppercase font-bold">Exact</th>
                  <th className="px-3 py-2.5 text-center text-[10px] uppercase font-bold">Top5</th>
                  {Object.entries(lsLabels).map(([k, l]) => (
                    <th key={k} className="px-3 py-2.5 text-center text-[10px] uppercase font-bold">{l} %</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lsBoard.map((w, idx) => (
                  <tr key={w.window_name} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-2 border-b font-semibold">{w.window_name}</td>
                    <td className="px-3 py-2 border-b text-center font-mono font-bold">{w.total_score}/{w.max_score}</td>
                    <td className="px-3 py-2 border-b text-center">
                      <span className={`font-mono font-bold ${w.pct >= 40 ? 'text-emerald-600' : 'text-gray-500'}`}>{w.pct}%</span>
                    </td>
                    <td className="px-3 py-2 border-b text-center font-mono text-emerald-600 font-bold">{w.exact_hits}</td>
                    <td className="px-3 py-2 border-b text-center font-mono text-blue-600 font-bold">{w.top5_hits}</td>
                    {Object.keys(lsLabels).map(lk => {
                      const pb = w.per_ball?.[lk] || {};
                      return (
                        <td key={lk} className="px-3 py-2 border-b text-center font-mono font-bold text-xs">{pb.pct || 0}%</td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
